import { useQuery, useQueries } from '@tanstack/react-query'
import { get } from 'aws-amplify/api'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'

export interface DRELineItem {
  code: string
  label: string
  value: number
  type: 'revenue' | 'expense' | 'deduction' | 'subtotal' | 'bottom_line'
  level: number
  parentCode: string | null
  order: number
}

export interface DREComparisonLineItem extends DRELineItem {
  baseValue: number
  compareValue: number
  variance: number
  variancePercent: number
}

export interface DREYearlyLineItem {
  code: string
  label: string
  total: number
  monthlyValues: number[]
  monthLabels: string[]
  type: string
  level: number
}

export interface DRECalculations {
  receitaBruta: number
  receitaLiquida: number
  lucroBruto: number
  lucroLiquido: number
  margemBruta?: number
  margemLiquida?: number
}

export interface DREDataParams {
  mode: 'monthly' | 'yearly' | 'comparison' | 'yoy' | 'accumulated'
  year: number
  month?: number
  endMonth?: number
  compareYear?: number
  compareMonth?: number
}

// Response types
export interface DREResponse {
  mode: string
  period?: { year: number; month: number; label: string }
  periods?: {
    base: { year: number; month: number; label: string }
    compare: { year: number; month: number; label: string }
  }
  lineItems: DRELineItem[] | DREComparisonLineItem[] | DREYearlyLineItem[]
  calculations: DRECalculations | any
  metadata: {
    lastSyncedAt: string
    dataSource: string
  }
}

// Query key factory
export const dreKeys = {
  all: ['dre'] as const,
  mode: (mode: string, params: object) => ['dre', mode, params] as const
}

// Fetch DRE data from API
const fetchDREData = async (params: DREDataParams): Promise<DREResponse> => {
  const queryParams = new URLSearchParams()
  queryParams.append('mode', params.mode)
  queryParams.append('year', params.year.toString())

  if (params.month !== undefined) {
    queryParams.append('month', params.month.toString())
  }

  if (params.endMonth !== undefined) {
    queryParams.append('endMonth', params.endMonth.toString())
  }

  if (params.compareYear !== undefined) {
    queryParams.append('compareYear', params.compareYear.toString())
  }

  if (params.compareMonth !== undefined) {
    queryParams.append('compareMonth', params.compareMonth.toString())
  }

  const fullPath = `/monumenta/sig-data/dre?${queryParams.toString()}`

  try {
    const { response } = await get({
      apiName: 'monumenta',
      path: fullPath,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const result = await response
    const data = await result.body.json() as any

    return data as DREResponse
  } catch (error) {
    console.error('Failed to fetch DRE data:', error)
    throw error
  }
}

// Custom hook for DRE data
export const useDREData = (params: DREDataParams) => {
  const enabled = !!(
    params.year &&
    (params.mode === 'yearly' || params.month !== undefined)
  )

  return useQuery({
    queryKey: dreKeys.mode(params.mode, params),
    queryFn: () => fetchDREData(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
    retry: 3
  })
}

// Helper to generate month/year pairs for accumulated period
const generateMonthYearPairs = (
  startYear: number,
  startMonth: number, // 1-based
  endYear: number,
  endMonth: number // 1-based
): Array<{ year: number; month: number }> => {
  const pairs: Array<{ year: number; month: number }> = []
  let currentYear = startYear
  let currentMonth = startMonth

  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    pairs.push({ year: currentYear, month: currentMonth })

    currentMonth++
    if (currentMonth > 12) {
      currentMonth = 1
      currentYear++
    }
  }

  return pairs
}

// Aggregate multiple DRE responses into one
const aggregateDREData = (responses: DREResponse[]): DREResponse => {
  if (responses.length === 0) {
    throw new Error('No data to aggregate')
  }

  if (responses.length === 1) {
    return responses[0]
  }

  // Use the first response as a template
  const template = responses[0]
  const lineItemsMap = new Map<string, DRELineItem>()

  // Initialize map with first response
  template.lineItems.forEach((item: any) => {
    lineItemsMap.set(item.code, { ...item, value: 0 })
  })

  // Aggregate values from all responses
  responses.forEach(response => {
    response.lineItems.forEach((item: any) => {
      const existing = lineItemsMap.get(item.code)
      if (existing) {
        existing.value += item.value || 0
      }
    })
  })

  // Calculate aggregated calculations
  const aggregatedCalculations: DRECalculations = responses.reduce((acc, response) => {
    return {
      receitaBruta: (acc.receitaBruta || 0) + (response.calculations?.receitaBruta || 0),
      receitaLiquida: (acc.receitaLiquida || 0) + (response.calculations?.receitaLiquida || 0),
      lucroBruto: (acc.lucroBruto || 0) + (response.calculations?.lucroBruto || 0),
      lucroLiquido: (acc.lucroLiquido || 0) + (response.calculations?.lucroLiquido || 0)
    }
  }, { receitaBruta: 0, receitaLiquida: 0, lucroBruto: 0, lucroLiquido: 0 } as DRECalculations)

  // Calculate margins
  if (aggregatedCalculations.receitaLiquida !== 0) {
    aggregatedCalculations.margemBruta =
      (aggregatedCalculations.lucroBruto / aggregatedCalculations.receitaLiquida) * 100
    aggregatedCalculations.margemLiquida =
      (aggregatedCalculations.lucroLiquido / aggregatedCalculations.receitaLiquida) * 100
  }

  return {
    mode: 'accumulated',
    period: {
      year: template.period?.year || 0,
      month: template.period?.month || 0,
      label: 'Período Acumulado'
    },
    lineItems: Array.from(lineItemsMap.values()),
    calculations: aggregatedCalculations,
    metadata: template.metadata
  }
}

// Custom hook for accumulated DRE data (frontend aggregation)
export const useAccumulatedDREData = (
  startYear: number,
  startMonth: number, // 0-based
  endYear: number,
  endMonth: number // 0-based
) => {
  // Generate all month/year combinations
  const monthYearPairs = generateMonthYearPairs(
    startYear,
    startMonth + 1, // Convert to 1-based
    endYear,
    endMonth + 1 // Convert to 1-based
  )

  // Fetch data for each month
  const queries = useQueries({
    queries: monthYearPairs.map(({ year, month }) => ({
      queryKey: dreKeys.mode('monthly', { year, month }),
      queryFn: () => fetchDREData({ mode: 'monthly', year, month }),
      staleTime: 1000 * 60 * 5,
      retry: 3
    }))
  })

  // Check if all queries are done
  const isLoading = queries.some(q => q.isLoading)
  const error = queries.find(q => q.error)?.error || null
  const allData = queries.map(q => q.data).filter(Boolean) as DREResponse[]

  // Aggregate data when all queries are successful
  const data = allData.length === monthYearPairs.length ? aggregateDREData(allData) : null

  return {
    data,
    isLoading,
    error,
    refetch: () => queries.forEach(q => q.refetch())
  }
}
